

























import { Vue, Component, Prop, Watch, Ref } from 'vue-property-decorator';
import type { IEditorContent, ISection, IMergeValues, EBlockWidth } from '@getaccept/editor-lib';
import { EditorHelper, EContext } from '@getaccept/editor-lib';
import type { IRecipient } from '@lib-shared/types/Recipient';
import type { IUser } from '@lib-shared/users/types/user';
import type { IEditorRecipientInput } from '@lib-shared/types/editor-recipient-input';
import EditorSection from '../components/EditorSection.vue';
import { EditorViewInputAction, namespace } from '../store/editor-view-input.constants';
import { editorViewStore } from '../store/editor-view.store';
import { editorViewInputState } from '../store/editor-view-input.store';
import { EditorViewAction } from '../store/editor-view.constants';

@Component({
  components: {
    EditorSection,
  },
})
export default class EditorBlockView extends Vue {
  @Prop() authToken: { jwt: string; exp: number };
  @Prop() isPreview: boolean;
  @Prop() documentId: string;
  @Prop() authId: string;
  @Prop() content: IEditorContent;
  @Prop() focusedRequiredId?: string;
  @Prop() mergeValues: IMergeValues;
  @Prop({ default: false }) isPdfViewer: boolean;
  @Prop() pageWidth: number;
  @Prop({ default: false }) isBlockToggled: boolean;
  @Prop({ default: false }) isSigningSite: boolean;
  @Prop() blockId: string;
  @Prop() sender: IUser;
  @Prop() recipient: IRecipient;
  @Prop({ default: () => [] }) recipientInput!: IEditorRecipientInput[];
  @Prop({ default: () => EContext.Document }) context: EContext;

  @Ref('editorContainer') editorContainer: HTMLElement;
  blockWidthClass: EBlockWidth = null;

  get tokenIsSynced() {
    return editorViewStore.state.tokenIsSynced;
  }

  get documentStatusSigned() {
    return editorViewStore?.state.documentStatusSigned;
  }

  get documentRecalled() {
    return editorViewStore?.state.documentRecalled;
  }

  get previewTokenExpired() {
    return editorViewStore?.state.previewTokenExpired;
  }

  get updatingInputs(): IEditorRecipientInput[] {
    return editorViewInputState?.updatingInputs || [];
  }

  get failedInputUpdates(): IEditorRecipientInput[] {
    return editorViewInputState?.failedInputUpdates || [];
  }

  get alert(): string {
    return editorViewInputState?.alert;
  }

  get sections(): ISection[] {
    return this.content?.sections;
  }

  @Watch('pageWidth', { immediate: true }) watchPageWidth() {
    Vue.nextTick(() => {
      this.setBlockWidth(this.pageWidth || this.editorContainer?.offsetWidth);
    });
  }

  @Watch('alert') watchAlert() {
    if (this.alert) {
      this.$emit('alert', this.alert);
      editorViewStore.dispatch(`${namespace}/${EditorViewInputAction.SetAlert}`, null);
    }
  }

  @Watch('authToken', { immediate: true }) watchAuthToken() {
    editorViewStore.dispatch(EditorViewAction.SetToken, this.authToken);

    if (
      this.authToken?.jwt === editorViewStore.state.authToken?.jwt &&
      this.authToken?.exp === editorViewStore.state.authToken?.exp
    ) {
      editorViewStore.dispatch(EditorViewAction.SetTokenIsSynced, true);
    }
  }

  @Watch('isPreview', { immediate: true }) watchIsPreview() {
    editorViewStore.dispatch(EditorViewAction.SetIsPreview, this.isPreview);
  }

  @Watch('documentId', { immediate: true }) watchDocumentId() {
    editorViewStore.dispatch(EditorViewAction.SetDocumentId, this.documentId);
  }

  @Watch('recipient', { immediate: true }) watchRecipientId() {
    if (this.recipient) {
      editorViewStore.dispatch(EditorViewAction.SetRecipientId, this.recipient.id);
    }
  }

  @Watch('authId', { immediate: true }) watchAuthId() {
    editorViewStore.dispatch(EditorViewAction.SetAuthId, this.authId);
  }

  @Watch('tokenIsSynced') watchTokenIsSynced() {
    if (!this.tokenIsSynced) {
      this.$emit('update-token', editorViewStore.state.authToken);
    }
  }

  @Watch('documentStatusSigned') watchDocumentStatusSigned() {
    if (this.documentStatusSigned) {
      this.$emit('set-document-status-signed');
      editorViewStore.dispatch(EditorViewAction.EmitSetDocumentStatusSigned, null);
    }
  }

  @Watch('documentRecalled') watchDocumentRecalled() {
    if (this.documentRecalled) {
      this.$emit('set-document-recalled');
      editorViewStore.dispatch(EditorViewAction.EmitDocumentRecalled, null);
    }
  }

  @Watch('previewTokenExpired') watchPreviewTokenExpired() {
    if (this.previewTokenExpired) {
      this.$emit('set-preview-token-expired');
      editorViewStore.dispatch(EditorViewAction.EmitPreviewTokenExpired, null);
    }
  }

  mounted() {
    Vue.nextTick(() => {
      this.$emit('loaded');
    });
  }

  handleUpdateInputValue({ id: fieldId, value }: { id: string; value: string }) {
    this.$emit(
      'update-recipient-input',
      this.recipientInput.filter(input => input.fieldId !== fieldId).concat({ fieldId, value })
    );
    editorViewStore.dispatch(`${namespace}/${EditorViewInputAction.UpdateInputValue}`, {
      value,
      fieldId,
      blockId: this.blockId,
    });
  }

  setBlockWidth(width: number) {
    const blockWidth = EditorHelper.getBlockWidth(width);
    this.blockWidthClass = EditorHelper.getBlockWidthClass(blockWidth);
  }
}
